import React, { useState, useEffect } from "react";
import "./OnboardingQuestions.css";
import { websiteImages } from "../../../config/config";
import {
  getOnboardingFlow,
  seeMyPlan,
  submitOnboardingAnswers,
} from "../../../services/clientWebsite";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../../components/Toast";
import { useNavigate } from "react-router-dom";
import { updateOnboardingCompleted } from "../../../redux/slices/authSlice";

// Access API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Main component for handling onboarding questions
const Onboarding = () => {
  // State for managing onboarding sections
  const [sections, setSections] = useState([]);
  // State for the currently displayed section
  const [currentSection, setCurrentSection] = useState(null);
  // State for the current section index
  const [sectionIndex, setSectionIndex] = useState(0);
  // State for storing user answers
  const [answers, setAnswers] = useState({});
  // State for loading indicator
  const [loading, setLoading] = useState(false);
  // State for error messages
  const [error, setError] = useState(null);
  // State for caching conditional questions
  const [conditionalQuestionsCache, setConditionalQuestionsCache] = useState({});
  // State for tracking if 4th day has been added
  const [fourthDayAdded, setFourthDayAdded] = useState(false);
  // Get user data from Redux store
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Scroll to top of section when sectionIndex changes
  useEffect(() => {
    const sectionElement = document.querySelector(".section-page");
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [sectionIndex]);

  // Fetch onboarding flow sections on component mount
  useEffect(() => {
    async function fetchFlow() {
      setLoading(true);
      try {
        const response = await getOnboardingFlow();
        // Ensure sections are an array
        const apiSections = Array.isArray(response.data) ? response.data : [];

        if (apiSections.length > 0) {
          // Transform API response to match UI structure
          const transformedSections = transformAPIResponse(apiSections);
          setSections(transformedSections);
          setCurrentSection(transformedSections[0]);
          setSectionIndex(0);
          setAnswers({}); // Reset answers for new flow
        } else {
          throw new Error("No sections found in API response");
        }
      } catch (e) {
        console.error("Error fetching flow:", e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    fetchFlow();
  }, []);

  // Prepare payload for submitting answers to API
  const prepareAnswerPayload = (answers, sections, userId = user.id) => {
    const payload = {
      user_id: userId,
      fourthDayAdded: fourthDayAdded,
      sections: [],
    };

    for (const section of sections) {
      const sectionAnswers = [];
      if (section.questions && section.questions.length > 0) {
        for (const question of section.questions) {
          const questionId = question.id;
          const answerValue = answers[questionId];

          // Include non-empty answers
          if (
            answerValue !== undefined &&
            answerValue !== null &&
            answerValue !== ""
          ) {
            let formattedAnswer = answerValue;
            // Ensure checkbox answers are arrays
            if (question.type === "checkbox" && !Array.isArray(answerValue)) {
              formattedAnswer = [answerValue];
            } else if (Array.isArray(answerValue) && answerValue.length === 0) {
              continue;
            }

            // Add main question answer
            sectionAnswers.push({
              question_id: questionId,
              question_title: question.title,
              answer: formattedAnswer,
              parent_id: null, // Main questions have no parent
            });

            // Handle conditional questions
            if (
              question.hasConditionalQuestions &&
              typeof answerValue === "string"
            ) {
              const key = `${questionId}_${answerValue}`;
              const conditionalQuestion = conditionalQuestionsCache[key];
              if (conditionalQuestion) {
                const conditionalAnswer = answers[conditionalQuestion.id];
                if (
                  conditionalAnswer !== undefined &&
                  conditionalAnswer !== null &&
                  conditionalAnswer !== ""
                ) {
                  let formattedConditionalAnswer = conditionalAnswer;
                  // Ensure conditional checkbox answers are arrays
                  if (
                    conditionalQuestion.type === "checkbox" &&
                    !Array.isArray(conditionalAnswer)
                  ) {
                    formattedConditionalAnswer = [conditionalAnswer];
                  }

                  sectionAnswers.push({
                    question_id: conditionalQuestion.id,
                    question_title: conditionalQuestion.title,
                    answer: formattedConditionalAnswer,
                    parent_id: questionId, // Link to parent question
                  });
                }
              }
            }
          }
        }
      }
      if (sectionAnswers.length > 0) {
        payload.sections.push({
          section_id: section.id,
          section_title: section.title,
          answers: sectionAnswers,
        });
      }
    }
    return payload;
  };

  // Submit answers to the server
  const submitAnswers = async () => {
    try {
      const payload = prepareAnswerPayload(answers, sections, user?.id);
      console.log("Submitting payload:", payload);

      // const response = await submitOnboardingAnswers(payload);

      // console.log("Submission successful:", response.data);

      // if (response.success) {
      //   notify.success(response.message);
      //   // Update Redux state to mark onboarding as completed
      //   dispatch(
      //     updateOnboardingCompleted({
      //       onboardingCompleted: true,
      //     })
      //   );
      //   navigate(-1); // Navigate to previous page
      // }

      // return response.data;
    } catch (error) {
      console.error("Submission error:", error);
      notify.error(error.response?.data?.message || error.message);
      throw error;
    }
  };

  // Transform API response to match UI structure
  const transformAPIResponse = (apiSections) => {
    return apiSections.map((section) => ({
      id: section.id,
      title: section.section_title,
      subtitle: section.subtitle || "",
      questions:
        section.questions?.map((question) => ({
          id: question.id,
          title: question.question_title,
          subtitle: question.question_description || "",
          type: mapQuestionType(question.question_type),
          options: extractOptionsFromAnswerFields(question.answer_fields),
          required: question.required !== false,
          maxSelect: question.max_select || null,
          minSelect: question.min_select || null,
          placeholder: question.answer_fields?.placeholder || "",
          gridClass: question.answer_fields?.gridClass || "",
          specialLogic: question.answer_fields?.specialLogic || null,
          hasConditionalQuestions:
            question.conditional_questions &&
            Object.keys(question.conditional_questions).length > 0,
          conditionalQuestions: question.conditional_questions || {},
          features: parseFeatures(question.question_description),
          ctaText: question.ctaText || "",
          buttonText: question.button_text || "",
          buttonType: question.button_type || "",
          disclaimer: parseDisclaimer(question.question_description),
        })) || [],
    }));
  };

  // Parse features from question description for CTA buttons
  const parseFeatures = (description) => {
    if (!description) return [];

    const lines = description.split("\n");
    const features = [];

    for (const line of lines) {
      if (
        line.includes("✅") ||
        line.includes("Custom") ||
        line.includes("Tailored") ||
        line.includes("Coaching")
      ) {
        const cleanLine = line.replace("✅", "").trim();
        if (cleanLine) {
          features.push({
            icon: "✅",
            title: cleanLine,
            description: "",
          });
        }
      }
    }

    return features;
  };

  // Parse disclaimer from question description
  const parseDisclaimer = (description) => {
    if (!description) return "";

    const lines = description.split("\n");
    const disclaimerLine = lines.find(
      (line) =>
        line.includes("No credit card") ||
        line.includes("required") ||
        line.includes("Input:")
    );

    return disclaimerLine || "";
  };

  // Extract options from answer_fields
  const extractOptionsFromAnswerFields = (answerFields) => {
    if (!answerFields) return [];

    if (Array.isArray(answerFields)) {
      return answerFields.map((field) => field.value);
    }

    if (answerFields.options && Array.isArray(answerFields.options)) {
      return answerFields.options;
    }

    return [];
  };

  // Map API question types to UI component types
  const mapQuestionType = (apiType) => {
    const typeMap = {
      single_choice: "radio",
      multiple_choice: "checkbox",
      text: "textarea",
      email: "email",
      date: "date",
      message: "message",
      cta: "cta",
      button: "cta",
    };
    return typeMap[apiType] || "textarea";
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Load a specific section
  const loadSection = (section, index) => {
    setCurrentSection(section);
    setSectionIndex(index);
  };

  // Navigate to the next section
  const handleNext = () => {
    const nextIndex = sectionIndex + 1;
    if (nextIndex >= sections.length) return;
    loadSection(sections[nextIndex], nextIndex);
  };

  // Navigate to the previous section
  const handleBack = () => {
    if (sectionIndex === 0) return;
    const prevIndex = sectionIndex - 1;
    loadSection(sections[prevIndex], prevIndex);
  };

  // Handle answer changes
  const handleAnswerChange = async (field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
    const question = currentSection?.questions?.find((q) => q.id === field);
    if (
      question &&
      question.conditionalQuestions &&
      value &&
      Object.keys(question.conditionalQuestions).length > 0
    ) {
      await processConditionalQuestion(field, value, question);
    }
  };

  // Toggle radio selection with deselect functionality
  const handleRadioToggle = (field, value) => {
    if (answers[field] === value) {
      handleAnswerChange(field, null);
    } else {
      handleAnswerChange(field, value);
    }
  };

  // Toggle checkbox selection
  const handleCheckboxToggle = (field, value, maxSelect) => {
    const currentValues = answers[field] || [];
    let newValues;
    if (currentValues.includes(value)) {
      newValues = currentValues.filter((v) => v !== value);
    } else {
      if (maxSelect && currentValues.length >= maxSelect) return;
      newValues = [...currentValues, value];
    }
    handleAnswerChange(field, newValues);
  };

  // Handle special checkbox logic (e.g., "No restrictions")
  const handleSpecialCheckboxLogic = (field, value, specialLogic) => {
    const currentValues = answers[field] || [];
    if (specialLogic === "noRestrictions") {
      if (value === "No restrictions") {
        handleAnswerChange(field, [value]);
      } else {
        const filtered = currentValues.filter(
          (item) => item !== "No restrictions"
        );
        if (filtered.includes(value)) {
          handleAnswerChange(
            field,
            filtered.filter((item) => item !== value)
          );
        } else {
          handleAnswerChange(field, [...filtered, value]);
        }
      }
    } else {
      handleCheckboxToggle(field, value);
    }
  };

  const handleSeeMyPlan = async () => {
    try {
      const payload = prepareAnswerPayload(answers, sections, user?.id);
      console.log("See My Plan payload:", payload);
      const response = await seeMyPlan(payload);
      console.log("See My Plan response:", response.data);
      if (response.success) {
        notify.success(response.message);
        // Optionally navigate to a plan page or display the plan
        // navigate("/my-plan");
      } else {
        notify.error(response.message);
      }
    } catch (error) {
      console.error("See My Plan error:", error);
      notify.error(error.response?.data?.message || error.message);
    }
  };

  // Handle CTA button clicks
  const handleButtonClick = (e, buttonType, buttonText, questionId) => {
    e.preventDefault();
    console.log(`Button clicked: ${buttonText} of type ${buttonType}`);

    // Check if this is the "Add 4th day" button
    if (buttonText === "Add 4th day") {
      setFourthDayAdded(true);
      // Record this as an answer so the section becomes valid
      setAnswers((prev) => ({ ...prev, [questionId]: buttonText }));
      return;
    }

    if (buttonType === "submit") {
      // Record this as an answer for validation

      console.log("dchbud", buttonText);
      // checkout to purchase 49/week plan with stripe

    }

    if (buttonType === "next" && buttonText === "See My Plan") {
      handleSeeMyPlan();
    }

    if (buttonType === "chat" && buttonText === "Chat with Coach") {
      // handleChatToCoach();
    }
  };

  // Handle Chat to Coach button click
  const handleChatToCoach = (e) => {
    e.preventDefault();
    console.log("Chat to Coach clicked!");
    // Add your chat to coach logic here
    // For example: open a chat widget, navigate to a chat page, etc.
  };

  // Process conditional questions based on user selection
  async function processConditionalQuestion(
    questionId,
    selectedValue,
    question
  ) {
    const key = `${questionId}_${selectedValue}`;
    if (conditionalQuestionsCache[key]) return conditionalQuestionsCache[key];

    try {
      const selectedIndex = question.options.indexOf(selectedValue);
      const conditionalKey = `${questionId}-${selectedIndex}`;

      if (
        question.conditionalQuestions &&
        question.conditionalQuestions[conditionalKey]
      ) {
        const conditionalQs = question.conditionalQuestions[conditionalKey];
        if (conditionalQs && conditionalQs.length > 0) {
          const conditionalQuestion = conditionalQs[0];
          const transformedConditional = {
            id: conditionalQuestion.id,
            title: conditionalQuestion.question_title,
            subtitle: conditionalQuestion.question_description || "",
            type: mapQuestionType(conditionalQuestion.question_type),
            options: extractOptionsFromAnswerFields(
              conditionalQuestion.answer_fields
            ),
            required: conditionalQuestion.required !== false,
            maxSelect: conditionalQuestion.max_select || conditionalQuestion.answer_fields?.maxSelect || null,
            minSelect: conditionalQuestion.min_select || conditionalQuestion.answer_fields?.minSelect || null,
            placeholder: conditionalQuestion.answer_fields?.placeholder || "",
            ctaText: conditionalQuestion.ctaText || "",
            buttonText: conditionalQuestion.button_text || "",
            buttonType: conditionalQuestion.button_type || "",
            features: parseFeatures(conditionalQuestion.question_description),
            disclaimer: parseDisclaimer(conditionalQuestion.question_description),
          };
          setConditionalQuestionsCache((prev) => ({
            ...prev,
            [key]: transformedConditional,
          }));
          return transformedConditional;
        }
      }

      return null;
    } catch (e) {
      console.error("Error processing conditional question:", e);
      return null;
    }
  }

  // Render conditional question
  const renderConditionalQuestion = (questionId, selectedValue) => {
    const key = `${questionId}_${selectedValue}`;
    const conditionalQuestion = conditionalQuestionsCache[key];
    if (!conditionalQuestion) return null;
    return (
      <div className="conditional-question">
        {renderQuestion(conditionalQuestion, true)}
      </div>
    );
  };

  // Validate current section
  const isSectionValid = () => {
    if (!currentSection || !currentSection.questions) return false;
    for (let question of currentSection.questions) {
      // Skip message questions, but validate CTA questions if they're required
      if (question.type === "message") continue;

      let answer = answers[question.id];

      // For CTA questions, check if they have been clicked/answered
      if (question.type === "cta") {
        if (question.required && !answer) {
          return false;
        }
        continue;
      }

      // Validate required fields
      if (question.required) {
        if (question.type === "checkbox") {
          if (!answer || !Array.isArray(answer) || answer.length === 0) {
            return false;
          }
        } else {
          if (answer === undefined || answer === null || answer === "") {
            return false;
          }
        }
      }

      // Validate checkbox min/max selections
      if (question.type === "checkbox" && Array.isArray(answer)) {
        if (question.minSelect && answer.length < question.minSelect) {
          return false;
        }
        if (question.maxSelect && answer.length > question.maxSelect) {
          return false;
        }
      }

      // Validate conditional questions
      if (
        question.hasConditionalQuestions &&
        typeof answer === "string" &&
        answer
      ) {
        const key = `${question.id}_${answer}`;
        const conditionalQ = conditionalQuestionsCache[key];
        if (conditionalQ && conditionalQ.required) {
          const conditionalAnswer = answers[conditionalQ.id];
          if (!conditionalAnswer) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Calculate progress percentage
  const getProgress = () => {
    if (!sections.length) return 0;
    let completedSections = 0;
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      let sectionValid = true;
      if (section.questions) {
        for (let question of section.questions) {
          if (question.type === "cta" || question.type === "message") continue;

          if (question.required) {
            const answer = answers[question.id];
            if (
              (question.type === "checkbox" &&
                (!answer || !Array.isArray(answer) || answer.length === 0)) ||
              answer === undefined ||
              answer === null ||
              answer === ""
            ) {
              sectionValid = false;
              break;
            }
            if (question.hasConditionalQuestions && answer) {
              const key = `${question.id}_${answer}`;
              const conditionalQ = conditionalQuestionsCache[key];
              if (conditionalQ && conditionalQ.required) {
                const conditionalAnswer = answers[conditionalQ.id];
                if (!conditionalAnswer) {
                  sectionValid = false;
                  break;
                }
              }
            }
          }
        }
      }
      if (sectionValid) {
        completedSections++;
      } else {
        break;
      }
    }
    return (completedSections / sections.length) * 100;
  };

  // Render individual question
  const renderQuestion = (question, isConditional = false) => {
    if (!question) return null;

    const {
      id,
      title,
      subtitle,
      type,
      options = [],
      maxSelect,
      minSelect,
      placeholder,
      gridClass,
      specialLogic,
      features,
      ctaText,
      buttonText,
      buttonType,
      disclaimer,
    } = question;

    const containerClass = isConditional
      ? "conditional-question question-block"
      : "question-block";

    switch (type) {
      case "radio":
        return (
          <div key={id} className={containerClass}>
            <h3 className="question-title">{title}</h3>
            {subtitle && <p className="question-subtitle">{subtitle}</p>}
            <div
              className={`options-grid ${gridClass || (options.length > 3 ? "two-column" : "")
                }`}
            >
              {options.map((option, idx) => (
                <div
                  key={idx}
                  className={`option-card ${answers[id] === option ? "selected" : ""
                    }`}
                  onClick={() => handleRadioToggle(id, option)}
                >
                  <input
                    type="radio"
                    checked={answers[id] === option}
                    readOnly
                  />
                  <span className="option-text">{option}</span>
                </div>
              ))}
            </div>
            {question.hasConditionalQuestions &&
              answers[id] &&
              renderConditionalQuestion(id, answers[id])}
          </div>
        );

      case "checkbox":
        return (
          <div key={id} className={containerClass}>
            <h3 className="question-title">{title}</h3>
            {subtitle && <p className="question-subtitle">{subtitle}</p>}
            {maxSelect && (
              <div className="multi-select-info">Select up to {maxSelect}</div>
            )}
            <div
              className={`options-grid ${gridClass || (options.length > 3 ? "two-column" : "")
                }`}
            >
              {options.map((option, idx) => (
                <div
                  key={idx}
                  className={`option-card ${(answers[id] || []).includes(option) ? "selected" : ""
                    }`}
                  onClick={() => {
                    if (specialLogic) {
                      handleSpecialCheckboxLogic(id, option, specialLogic);
                    } else {
                      handleCheckboxToggle(id, option, maxSelect, minSelect);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={(answers[id] || []).includes(option)}
                    readOnly
                  />
                  <span className="option-text">{option}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case "email":
      case "date":
        return (
          <div key={id} className={containerClass}>
            <h3 className="question-title">{title}</h3>
            {subtitle && <p className="question-subtitle">{subtitle}</p>}
            <input
              type={type}
              className="input-field"
              placeholder={placeholder}
              value={answers[id] || ""}
              onChange={(e) => handleAnswerChange(id, e.target.value)}
            />
          </div>
        );

      case "textarea":
        return (
          <div key={id} className={containerClass}>
            <h3 className="question-title">{title}</h3>
            {subtitle && <p className="question-subtitle">{subtitle}</p>}
            <textarea
              className="input-field"
              placeholder={placeholder}
              rows={4}
              value={answers[id] || ""}
              onChange={(e) => handleAnswerChange(id, e.target.value)}
            />
          </div>
        );

      case "message":
        return (
          <div key={id} className="completion-card">
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
        );

      case "cta":
        return (
          <div key={id} className="completion-card">
            <h2>{title}</h2>
            {subtitle && features.length === 0 && <p>{subtitle}</p>}
            {
              features && features.length > 0 && (
                <div className="features-grid">
                  {features.map((feature, idx) => (
                    <div key={idx} className="feature-item">
                      <span className="feature-icon">{feature.icon}</span>
                      <div className="feature-title">{feature.title}</div>
                      {feature.description && (
                        <div className="feature-desc">{feature.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              )
            }
            {
              disclaimer && (
                <p style={{ color: "#00ffff", marginTop: "1rem" }}>
                  {disclaimer}
                </p>
              )
            }
            {buttonText === "Add 4th day" ? (
              <div className="button-group" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  className="cta-button"
                  onClick={(e) => handleButtonClick(e, buttonType, buttonText, id)}
                  disabled={false}
                >
                  {buttonText}
                </button>
                <button
                  className="cta-button secondary"
                  onClick={handleChatToCoach}
                  style={{
                    backgroundColor: 'transparent',
                    border: '2px solid #00ffff',
                    color: '#00ffff'
                  }}
                >
                  Chat to Coach
                </button>
              </div>
            ) : (
              <button
                className="cta-button"
                onClick={(e) => handleButtonClick(e, buttonType, buttonText, id)}
                disabled={Object.keys(answers).length === 0}
              >
                {ctaText || buttonText || "Submit My Answers"}
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Render current section
  const renderSection = () => {
    if (!currentSection) return null;

    return (
      <div className="section-page">
        <h1 className="section-title-question">{currentSection.title}</h1>
        {currentSection.subtitle && (
          <p className="section-subtitle">{currentSection.subtitle}</p>
        )}
        <div className="questions-grid">
          {currentSection.questions?.map((question) =>
            renderQuestion(question)
          )}
        </div>
      </div>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading questions...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Questions</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // Render empty state
  if (!sections.length) {
    return (
      <div className="error-container">
        <h2>No Questions Available</h2>
        <p>Please contact support if this issue persists.</p>
      </div>
    );
  }

  // Render main onboarding UI
  return (
    <div
      style={{
        fontFamily: "Plus Jakarta Sans",
        background: "#111111",
        color: "#ffffff",
        minHeight: "100vh",
      }}
    >
      <div className="onboarding-container">
        {/* Header Section */}
        <div className="header">
          <div className="logo-Question">
            <img src={websiteImages.websiteLogo} alt="Logo" />
          </div>
          <div className="tagline">
            Your Personalized Fitness Journey Starts Here
          </div>
        </div>

        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-info">
            <span className="step-indicator">
              Section {sectionIndex + 1} of {sections.length}
            </span>
            <span className="progress-percentage">
              {Math.round(getProgress())}%
            </span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${getProgress()}%` }}
            />
          </div>
        </div>

        {/* Section Content */}
        {renderSection()}

        {/* Navigation Buttons */}
        <div className="actions">
          <button
            className="btn btn-back"
            onClick={handleBack}
            disabled={sectionIndex === 0}
          >
            ← Back
          </button>
          {sectionIndex < sections.length - 1 && (
            <button
              className="btn btn-next"
              onClick={handleNext}
              disabled={!isSectionValid()}
            >
              Next Section →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;